import type { OfferingSupply } from "@/lib/offerings/supplies";
import type { RequestNeed } from "@/lib/requests/needs";
import type { DealLineFacts } from "@/lib/deals/line-facts";
import { z } from "zod";
import { dealDocumentUrl, dealDocRef } from "@/lib/deals/document-download/refs";
import { COUNTERPARTY_KINDS } from "@/lib/deals/counterparty/types";

// ============================================================================
// Deal Status Enum - Broker-Managed Workflow
// ============================================================================

export const DealStatusEnum = z.enum([
  "reserved", // Auto-created from order book, quantity locked
  "rejected", // Rejected by seller
  "invalidated", // Deal no longer feasible after offering/request update
  "voided", // NEW: Deal voided (e.g., offering/request timed out), quantity released
  "inquiry_pending", // Initial inquiry received, Platform reviewing
  "verification", // Platform verifying offering/request compatibility
  "proposal_ready", // Platform has prepared a proposal for both parties
  "contract_review", // Contract draft ready for both parties to review and accept
  "awaiting_signatures", // Contracts sent, waiting for signatures
  "unb_pending", // Waiting for UNB (authority) approval
  "payment_processing", // Payment/escrow being processed
  "completed", // Deal fully completed
  "cancelled", // Deal cancelled
]);

export type DealStatus = z.infer<typeof DealStatusEnum>;

export const DealVisibilitySchema = z.enum(["internal", "shared"]);
export type DealVisibility = z.infer<typeof DealVisibilitySchema>;

export interface DealEmailPreview {
  role: "buyer" | "seller";
  recipient: { email: string; name: string | null } | null;
  subject: string;
  html: string;
  text: string;
  eligible: boolean;
  reason: string | null;
}

export interface DealEmailPreviews {
  buyer: DealEmailPreview;
  seller: DealEmailPreview;
}

// Status display names (German) - Broker-focused messaging
export const DEAL_STATUS_LABELS: Record<DealStatus, string> = {
  reserved: "Reserviert",
  rejected: "Abgelehnt",
  invalidated: "Nicht mehr gültig",
  voided: "Aufgelöst",
  inquiry_pending: "Anfrage eingegangen",
  verification: "Wird geprüft",
  proposal_ready: "Angebot wird vorbereitet",
  contract_review: "Vertragsentwurf prüfen",
  awaiting_signatures: "Warten auf Unterschriften",
  unb_pending: "UNB-Genehmigung ausstehend",
  payment_processing: "Zahlung in Bearbeitung",
  completed: "Abgeschlossen",
  cancelled: "Storniert",
};

// Status descriptions for more context (German)
export const DEAL_STATUS_DESCRIPTIONS: Record<DealStatus, string> = {
  reserved: "Menge reserviert durch Auto-Match. Warten auf Bestätigung.",
  rejected: "Dieser Vorgang wurde abgelehnt.",
  invalidated: "Dieser Vorgang ist aufgrund von Änderungen nicht mehr gültig.",
  voided: "Dieser Vorgang wurde aufgelöst. Die Menge wurde freigegeben.",
  inquiry_pending: "Ihre Anfrage wird von unserem Team bearbeitet.",
  verification: "Wir prüfen die Kompatibilität und erstellen ein Angebot.",
  proposal_ready: "Ein Angebot liegt vor. Bitte prüfen Sie die Details.",
  contract_review: "Bitte prüfen Sie den Vertragsentwurf und geben Sie Ihr Einverständnis.",
  awaiting_signatures: "Bitte unterschreiben Sie den Vertrag.",
  unb_pending: "Der Vertrag wird von der UNB geprüft.",
  payment_processing: "Die Zahlung wird über unser Treuhandkonto abgewickelt.",
  completed: "Die Transaktion wurde erfolgreich abgeschlossen.",
  cancelled: "Der Vorgang wurde storniert.",
};

// Status order for progress tracking (excluding cancelled/rejected/voided)
export const DEAL_STATUS_ORDER: DealStatus[] = [
  "reserved",
  "inquiry_pending",
  "verification",
  "proposal_ready",
  "contract_review",
  "awaiting_signatures",
  "unb_pending",
  "payment_processing",
  "completed",
];

/** Index in DEAL_STATUS_ORDER from which counterparty identity is revealed (contract shared) */
const REVEAL_THRESHOLD_INDEX = DEAL_STATUS_ORDER.indexOf("contract_review");

/** Whether the deal status is at or past the contract stage where identity is revealed */
export function isIdentityRevealed(status: string): boolean {
  const idx = DEAL_STATUS_ORDER.indexOf(status as DealStatus);
  if (idx === -1) return false;
  return idx >= REVEAL_THRESHOLD_INDEX;
}

/** Statuses where a deal is no longer actionable */
export const INACTIVE_STATUSES: DealStatus[] = [
  "completed",
  "cancelled",
  "rejected",
  "invalidated",
  "voided",
];

/** Filter deals to only active (in-progress) ones */
export function getActiveDeals<T extends { status: string }>(deals: T[]): T[] {
  return deals.filter((d) => !(INACTIVE_STATUSES as string[]).includes(d.status));
}

// ============================================================================
// Deal Event Types - Broker Workflow
// ============================================================================

export const DealEventTypeEnum = z.enum([
  "deal_created",
  "visibility_changed",
  "status_change",
  "price_adjusted",
  "message_sent",
  "document_added",
  "admin_note_updated",
  "proposal_sent",
  "clauses_updated",
  "deadline_changed",
  "side_promoted",
  "contract_drafted",
  "signature_received",
  "payment_initiated",
  "payment_confirmed",
  "completed",
  "cancelled",
  "reminder_sent",
]);

export type DealEventType = z.infer<typeof DealEventTypeEnum>;

// ============================================================================
// Document Schema (for buyer_documents / seller_documents)
// ============================================================================

export const DealDocumentSchema = z.object({
  name: z.string(),
  // Finding H3: new rows store a bucket-relative storage `path`; `url` is kept
  // for legacy rows (pre-backfill) and the non-storage invoice entry
  // (`/api/deals/[id]/invoice`). Downloads resolve via the document-url seam.
  path: z.string().optional(),
  url: z.string().optional(),
  type: z.string().optional(),
  uploaded_at: z.string().datetime(),
  uploaded_by: z.enum(["seller", "buyer"]).optional(),
});

export type DealDocument = z.infer<typeof DealDocumentSchema>;

// ============================================================================
// Deal Schema
// ============================================================================

/** Counterparty kind for a deal side — derived from the single-source
 *  COUNTERPARTY_KINDS so the API/DB/domain cannot drift. */
export const CounterpartyKindEnum = z.enum(COUNTERPARTY_KINDS);

/**
 * A deal's root. What it trades is its positions (DealPositionView), not part of the root: the
 * root's old single-pair columns (requirement_id, supply_line_id, quantity_unit, reserved_quantity,
 * final_quantity, final_price, final_price_unit) are retired. No command writes them
 * (internal.deal_columns() refuses them, pinned by deal-columns.integration.test.ts) and no code
 * reads them (deal-retired-columns.guard.test.ts), so they are not part of this schema. A later
 * migration drops them.
 */
export const DealSchema = z.object({
  id: z.string().uuid(),
  /** The deal's concurrency token: a command decided on it is refused once the deal moved on. */
  revision: z.number().int().positive(),
  // Real-side anchors — NULLable since Phase 2: set only for a `real` side. A
  // synthetic/proxy/landvest side leaves its anchor NULL and is identified by its
  // Party ref below. (request_id has been nullable since the order-book redesign.)
  offering_id: z.string().uuid().optional().nullable(),
  request_id: z.string().uuid().optional().nullable(),
  seller_id: z.string().uuid().optional().nullable(),
  buyer_id: z.string().uuid().optional().nullable(),
  status: DealStatusEnum,
  visibility: DealVisibilitySchema,
  visibility_changed_at: z.string().datetime().optional().nullable(),
  visibility_changed_by: z.string().uuid().optional().nullable(),

  // Counterparty (Party) model — supply = seller/offering side; demand = buyer/
  // request side. See lib/deals/counterparty/. `*_ref` is an entity-pointer URI
  // (null iff kind=synthetic); `*_intent` is a free-text ÖPM note.
  supply_kind: CounterpartyKindEnum.default("real"),
  supply_ref: z.string().optional().nullable(),
  supply_intent: z.string().optional().nullable(),
  demand_kind: CounterpartyKindEnum.default("real"),
  demand_ref: z.string().optional().nullable(),
  demand_intent: z.string().optional().nullable(),

  // Legacy timestamps (kept for backward compatibility)
  matched_at: z.string().datetime(),
  seller_approved_at: z.string().datetime().optional().nullable(),
  buyer_approved_at: z.string().datetime().optional().nullable(),
  completed_at: z.string().datetime().optional().nullable(),
  cancelled_at: z.string().datetime().optional().nullable(),

  // New broker workflow timestamps
  verification_started_at: z.string().datetime().optional().nullable(),
  proposal_sent_at: z.string().datetime().optional().nullable(),
  contract_sent_at: z.string().datetime().optional().nullable(),
  signatures_received_at: z.string().datetime().optional().nullable(),
  payment_initiated_at: z.string().datetime().optional().nullable(),

  // UNB (authority) approval tracking
  unb_submitted_at: z.string().datetime().optional().nullable(),
  unb_approved_at: z.string().datetime().optional().nullable(),
  unb_notes: z.string().optional().nullable(),

  // Deadline tracking (ARCH-026 §Deadlines). `deadline_at` NULL = untracked —
  // every row predating the feature, plus any deal an admin deliberately opted
  // out. The two flags make the hourly sweep send each notice exactly once.
  deadline_at: z.string().datetime().optional().nullable(),
  deadline_reminder_sent: z.boolean().optional().nullable(),
  deadline_lapsed_notice_sent: z.boolean().optional().nullable(),

  // Configurable Kaufvertrag clauses (ARCH-035). Nullable/optional so a row
  // read before the columns existed still parses; the renderer treats
  // null/undefined as "not selected", i.e. the pre-Phase-2 wording.
  down_payment_percent: z.number().int().optional().nullable(),
  condition_baurecht: z.boolean().optional().nullable(),
  condition_naturschutz: z.boolean().optional().nullable(),
  dingliche_sicherung: z.boolean().optional().nullable(),
  widerrufsrecht: z.boolean().optional().nullable(),

  // Platform fees
  platform_fee: z.number().optional().nullable(),
  platform_fee_type: z.enum(["fixed", "percentage"]).optional().nullable(),

  // Additional
  notes: z.string().optional().nullable(),
  cancellation_reason: z.string().optional().nullable(),

  // Invalidation tracking
  invalidated_at: z.string().datetime().optional().nullable(),
  invalidation_reason: z.string().optional().nullable(),

  // Documents (role-based visibility - handled at API level)
  buyer_documents: z.array(DealDocumentSchema).optional().nullable(),
  seller_documents: z.array(DealDocumentSchema).optional().nullable(),

  // Admin notes (only visible to Platform admin)
  admin_notes: z.string().optional().nullable(),

  // Order Book / Reservation fields
  reservation_created_at: z.string().datetime().optional().nullable(),
  auto_matched: z.boolean().optional().nullable(),
  match_score_at_creation: z.number().optional().nullable(),
  previous_status: z.string().optional().nullable(),

  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type Deal = z.infer<typeof DealSchema>;

// ============================================================================
// Deal Event Schema
// ============================================================================

export const DealEventSchema = z.object({
  id: z.string().uuid(),
  deal_id: z.string().uuid(),
  event_type: DealEventTypeEnum,
  actor_id: z.string().uuid().optional().nullable(),
  data: z.record(z.string(), z.unknown()).default({}),
  created_at: z.string().datetime(),
});

export type DealEvent = z.infer<typeof DealEventSchema>;

// ============================================================================
// Deal With Relations
// ============================================================================

/**
 * What is left of one row (get_remaining_quantities_needed, get_available_quantities): its figure,
 * as the row's scalar reader answers it (null for a row without a Menge), and whether an active
 * position of a reserving deal holds it, which leaves a row without a Menge nothing more to give
 * (SINGLE_DEAL_MODE).
 */
export interface RowLeft {
  readonly left: number | null;
  readonly held: boolean;
}

/**
 * One row pair a deal fulfils (deal_positions): what its parties and Sales read of it, its axis and
 * subject in the registry's words (DEAL_POSITIONS_EMBED, read through positionsFrom).
 */
export interface DealPositionView {
  id: string;
  /** The row of the deal's Anfrage it fulfils; null when the demand side is not real. */
  demand_line_id: string | null;
  /** The row of the deal's Angebot it books; null when the supply side is not real. */
  supply_line_id: string | null;
  axis_id: string;
  axis_key: string;
  axis_name: string | null;
  /** The axis' place in the registry's display order. */
  axis_sort: number | null;
  subject_id: string | null;
  subject_key: string | null;
  subject_name: string | null;
  /** The unit key its quantity and price are in. */
  unit: string;
  quantity: number | null;
  /** € per unit of `unit`; null until agreed. */
  unit_price: number | null;
  status: "active" | "released";
  released_at: string | null;
  release_reason: string | null;
  created_at: string;
}

export interface DealWithRelations extends Deal {
  /** The row pairs the deal fulfils, oldest first. */
  positions?: DealPositionView[];
  /** Admin-only capability derived from the same database guard as the write. */
  visibilityCanBeInternal?: boolean;
  visibilityRestrictionReason?: string | null;
  /** Admin-only: whether a share would be accepted (the deal still reserves), and why not. */
  visibilityCanBeShared?: boolean;
  visibilityShareBlockedReason?: string | null;
  /** What each position the deal trades is about (describeDealPositions), in order. Staff only. */
  line_facts?: DealLineFacts[];
  offering?: {
    id: string;
    /** When the enquiry entered the platform — the CRM's arrival stamp (Deals „Erstkontakt“). */
    created_at?: string | null;
    status: string | null;
    description: string | null;
    plz: string | null;
    bundesland: string | null;
    landkreis: string | null;
    kreis: string | null;
    gemeinde: string | null;
    naturraum_id: string | null;
    kompensationsraum_id: string | null;
    area_hectares: number | null;
    okokonto_document_url?: string | null;
    okokonto_document_name?: string | null;
    attributes?: Record<string, unknown> | null;
    /** What the Angebot offers: its rows (each position names one of them, `supply_line_id`). */
    supplies: OfferingSupply[];
    // Resolved metadata (from PostgREST nested selects)
    naturraum_detail?: { zone_code: string; name: string } | null;
  };
  request?: {
    id: string;
    /** When the enquiry entered the platform — the CRM's arrival stamp (Deals „Erstkontakt“). */
    created_at?: string | null;
    status: string | null;
    description: string | null;
    plz: string | null;
    bundesland: string | null;
    landkreis: string | null;
    kreis: string | null;
    naturraum_id: string | null;
    /** What the request needs: its lines (each position names one of them, `demand_line_id`). */
    lines: RequestNeed[];
    // Resolved metadata (from PostgREST nested selects)
    naturraum_detail?: { zone_code: string; name: string } | null;
  };
  seller?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    email?: string | null;
  };
  buyer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    email?: string | null;
  };
  events?: DealEvent[];
}

// ============================================================================
// Filter Schema
// ============================================================================

export const DealFilterSchema = z.object({
  status: DealStatusEnum.optional(),
  statuses: z.array(DealStatusEnum).optional(),
  seller_id: z.string().uuid().optional(),
  buyer_id: z.string().uuid().optional(),
  offering_id: z.string().uuid().optional(),

  // Pagination
  limit: z.number().int().positive().max(100).default(50),
  offset: z.number().int().nonnegative().default(0),
});

export type DealFilter = z.infer<typeof DealFilterSchema>;

// ============================================================================
// Approval Input
// ============================================================================

export const ApprovalInputSchema = z.object({
  deal_id: z.string().uuid(),
  approved: z.boolean(),
  notes: z.string().optional(),
  cancellation_reason: z.string().optional(), // If not approved
});

export type ApprovalInput = z.infer<typeof ApprovalInputSchema>;

// ============================================================================
// Deal Progress - Broker Workflow
// ============================================================================

export interface DealProgressStep {
  status: DealStatus;
  label: string;
  description: string;
  completed: boolean;
  current: boolean;
  timestamp?: string;
}

/**
 * Get deal progress steps for the broker workflow.
 * Maps new workflow timestamps to their respective statuses.
 */
export function getDealProgress(deal: Deal): DealProgressStep[] {
  const currentIndex = DEAL_STATUS_ORDER.indexOf(deal.status);

  return DEAL_STATUS_ORDER.map((status, index) => ({
    status,
    label: DEAL_STATUS_LABELS[status],
    description: DEAL_STATUS_DESCRIPTIONS[status],
    completed: index < currentIndex,
    current: index === currentIndex,
    timestamp: getTimestampForStatus(deal, status),
  }));
}

/**
 * Get the timestamp for a specific status from the deal object.
 */
function getTimestampForStatus(deal: Deal, status: DealStatus): string | undefined {
  switch (status) {
    case "reserved":
      return deal.reservation_created_at ?? deal.created_at;
    case "inquiry_pending":
      return deal.matched_at; // Uses matched_at as the inquiry creation time
    case "verification":
      return deal.verification_started_at ?? undefined;
    case "proposal_ready":
      return deal.proposal_sent_at ?? undefined;
    case "awaiting_signatures":
      return deal.signatures_received_at ?? undefined;
    case "unb_pending":
      return deal.unb_submitted_at ?? undefined;
    case "payment_processing":
      return deal.payment_initiated_at ?? undefined;
    case "completed":
      return deal.completed_at ?? undefined;
    default:
      return undefined;
  }
}

/**
 * Get the user's role in a deal.
 */
export type DealUserRole = "buyer" | "seller" | "admin" | "unknown";

export function getDealUserRole(deal: Deal, userId: string): DealUserRole {
  if (userId === deal.buyer_id) return "buyer";
  if (userId === deal.seller_id) return "seller";
  // Platform admin check would need the PLATFORM_ADMIN_ID constant
  return "unknown";
}

/**
 * Document types that are private to the buyer (not visible to seller).
 * The invoice is a commission agreement between the platform and buyer only.
 */
const BUYER_PRIVATE_DOC_TYPES = ["invoice"];

/** A visible deal document plus an authorized `downloadHref` (finding H3). */
export type DealDocumentWithHref = DealDocument & { downloadHref: string };

/**
 * Get documents visible to a specific user role, each carrying a `downloadHref`
 * that points at the authorized document-url seam (finding H3) instead of a raw
 * stored URL. The href uses the document's index within THIS visible list, which
 * the seam service resolves via the same `getVisibleDocuments(deal, role)`
 * ordering — so the index is stable across both sides.
 *
 * Filters out buyer-private documents (e.g. invoices) from the seller's view.
 */
export function getVisibleDocuments(deal: Deal, role: DealUserRole): DealDocumentWithHref[] {
  const buyerDocs = (deal.buyer_documents ?? []).map((d) => ({
    ...d,
    uploaded_by: "buyer" as const,
  }));
  const sellerDocs = (deal.seller_documents ?? []).map((d) => ({
    ...d,
    uploaded_by: "seller" as const,
  }));
  const sortByDate = (a: DealDocument, b: DealDocument) =>
    new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();

  let visible: DealDocument[];
  switch (role) {
    case "admin":
    case "buyer":
      visible = [...buyerDocs, ...sellerDocs].sort(sortByDate);
      break;
    case "seller": {
      // Exclude buyer-private documents (e.g. invoices) from seller's view
      const visibleBuyerDocs = buyerDocs.filter(
        (d) => !d.type || !BUYER_PRIVATE_DOC_TYPES.includes(d.type)
      );
      visible = [...visibleBuyerDocs, ...sellerDocs].sort(sortByDate);
      break;
    }
    default:
      return [];
  }

  return visible.map((doc, index) => ({
    ...doc,
    downloadHref: dealDocumentUrl(deal.id, dealDocRef(index), { download: true }),
  }));
}

// ============================================================================
// Available Actions - Shared pure logic for broker model
// ============================================================================

/** Statuses where users can cancel a deal */
export const CANCELLABLE_STATUSES: DealStatus[] = [
  "reserved",
  "inquiry_pending",
  "verification",
  "proposal_ready",
  "contract_review",
];

/**
 * Every lifecycle stage BEFORE the DocuSign envelope goes out.
 *
 * Two features hang off this one boundary, for the same reason — once the
 * envelope is out, the deal is in the parties' hands and the signed PDF, not
 * the row, is the contract:
 *
 * - **Clause lock (ARCH-035):** an admin may not change the Kaufvertrag clauses
 *   past this point, or the stored terms would disagree with what was signed.
 * - **Deadline tracking (ARCH-026 §Deadlines):** reminders and lapse notices
 *   stop here. Nagging a buyer to "act before the deadline" while they are in
 *   DocuSign is noise, and a lapse notice on a deal being actively signed is
 *   simply wrong.
 *
 * Derived from the lifecycle order so a new pre-signature stage is covered
 * without a second list to remember. A missing signing stage yields an EMPTY
 * list — a lock must fail closed, and `slice(0, -1)` would instead silently
 * unlock every stage but the last.
 */
const SIGNING_STAGE_INDEX = DEAL_STATUS_ORDER.indexOf("awaiting_signatures");
export const PRE_SIGNATURE_STATUSES: DealStatus[] =
  SIGNING_STAGE_INDEX === -1 ? [] : DEAL_STATUS_ORDER.slice(0, SIGNING_STAGE_INDEX);

export interface DealActions {
  canApprove: boolean;
  canReject: boolean;
  canCancel: boolean;
  canComplete: boolean;
  approvalType?: "seller" | "buyer";
}

/**
 * Check what actions are available for a deal in broker model.
 * Pure function usable from both server and client code.
 */
export function getAvailableActions(
  deal: { status: string; seller_id?: string | null; buyer_id?: string | null },
  userId: string
): DealActions {
  const isSeller = deal.seller_id === userId;
  const isBuyer = deal.buyer_id === userId;

  if (!isSeller && !isBuyer) {
    return {
      canApprove: false,
      canReject: false,
      canCancel: false,
      canComplete: false,
    };
  }

  const status = deal.status as DealStatus;

  // Users can only cancel in early stages
  if (CANCELLABLE_STATUSES.includes(status)) {
    return {
      canApprove: false,
      canReject: false,
      canCancel: true,
      canComplete: false,
    };
  }

  // No user actions available in later stages
  return {
    canApprove: false,
    canReject: false,
    canCancel: false,
    canComplete: false,
  };
}

// ============================================================================
// Role-Specific Progress Steps
// ============================================================================
// The UI shows different progress steps to sellers vs buyers, reflecting their
// actual involvement in the deal workflow. The underlying database status
// remains the single source of truth.

export type SellerProgressStep =
  | "identity_verification"
  | "eco_account_verification"
  | "contract_preparation"
  | "contract"
  | "unb_confirmation"
  | "payment_pending"
  | "completed";

export type BuyerProgressStep =
  | "identity_verification"
  | "eco_account_verification"
  | "contract_preparation"
  | "contract"
  | "unb_confirmation"
  | "payment_pending"
  | "completed";

export const SELLER_PROGRESS_STEPS: SellerProgressStep[] = [
  "identity_verification",
  "eco_account_verification",
  "contract_preparation",
  "contract",
  "unb_confirmation",
  "payment_pending",
  "completed",
];

export const BUYER_PROGRESS_STEPS: BuyerProgressStep[] = [
  "identity_verification",
  "eco_account_verification",
  "contract_preparation",
  "contract",
  "unb_confirmation",
  "payment_pending",
  "completed",
];

export const SELLER_STEP_LABELS: Record<SellerProgressStep, string> = {
  identity_verification: "Identität nachweisen",
  eco_account_verification: "Ökokonto verifizieren",
  contract_preparation: "Vertrag wird vorbereitet",
  contract: "Vertrag & Unterschrift",
  unb_confirmation: "UNB-Bestätigung abwarten",
  payment_pending: "Zahlungseingang bestätigen",
  completed: "Erfolgreich abgeschlossen",
};

export const BUYER_STEP_LABELS: Record<BuyerProgressStep, string> = {
  identity_verification: "Vertragsadresse angeben",
  eco_account_verification: "Ökokonto wird geprüft",
  contract_preparation: "Vertrag wird vorbereitet",
  contract: "Vertrag & Unterschrift",
  unb_confirmation: "UNB-Bestätigung abwarten",
  payment_pending: "Zahlung leisten",
  completed: "Erfolgreich abgeschlossen",
};

export const SELLER_STEP_DESCRIPTIONS: Record<SellerProgressStep, string> = {
  identity_verification: "Ihre Identität wird überprüft.",
  eco_account_verification: "Ihr Ökokonto wird vom Team verifiziert.",
  contract_preparation:
    "Unser Team bereitet den Kaufvertrag für Sie vor. Sobald der Vertrag fertig ist, werden Sie benachrichtigt.",
  contract: "Prüfen Sie den Vertragsentwurf und unterschreiben Sie den Kaufvertrag.",
  unb_confirmation:
    "Der Vertrag wird bei der UNB eingereicht. Sie werden benachrichtigt, sobald die Genehmigung vorliegt.",
  payment_pending: "Bitte bestätigen Sie den Eingang der Zahlung vom Käufer.",
  completed: "Die Transaktion wurde erfolgreich abgeschlossen.",
};

export const BUYER_STEP_DESCRIPTIONS: Record<BuyerProgressStep, string> = {
  identity_verification: "Bitte hinterlegen Sie Ihre Vertragsadresse.",
  eco_account_verification:
    "Wir prüfen das Ökokonto des Verkäufers, um sicherzustellen, dass die Punkte verfügbar sind.",
  contract_preparation:
    "Unser Team bereitet den Kaufvertrag für Sie vor. Sobald der Vertrag fertig ist, werden Sie benachrichtigt.",
  contract: "Prüfen Sie den Vertragsentwurf und unterschreiben Sie den Kaufvertrag.",
  unb_confirmation:
    "Der Vertrag wird bei der UNB eingereicht. Sie werden benachrichtigt, sobald die Genehmigung vorliegt.",
  payment_pending: "Bitte überweisen Sie den Kaufbetrag.",
  completed: "Die Transaktion wurde erfolgreich abgeschlossen.",
};

/**
 * Map database status to seller-specific progress step.
 */
export function getSellerStepForStatus(status: DealStatus): SellerProgressStep {
  switch (status) {
    case "reserved":
    case "inquiry_pending":
      return "identity_verification";
    case "verification":
      return "eco_account_verification";
    case "proposal_ready":
    case "contract_review":
      return "contract_preparation";
    case "awaiting_signatures":
      return "contract";
    case "unb_pending":
      return "unb_confirmation";
    case "payment_processing":
      return "payment_pending";
    case "completed":
      return "completed";
    default:
      return "identity_verification";
  }
}

/**
 * Map database status to buyer-specific progress step.
 */
export function getBuyerStepForStatus(status: DealStatus): BuyerProgressStep {
  switch (status) {
    case "reserved":
    case "inquiry_pending":
      return "identity_verification";
    case "verification":
      return "eco_account_verification";
    case "proposal_ready":
    case "contract_review":
      return "contract_preparation";
    case "awaiting_signatures":
      return "contract";
    case "unb_pending":
      return "unb_confirmation";
    case "payment_processing":
      return "payment_pending";
    case "completed":
      return "completed";
    default:
      return "identity_verification";
  }
}

/**
 * Get role-specific progress information for a deal.
 */
export interface RoleProgressInfo {
  steps: string[];
  labels: Record<string, string>;
  descriptions: Record<string, string>;
  currentStep: string;
  currentStepIndex: number;
}

export function getRoleProgress(status: DealStatus, role: "seller" | "buyer"): RoleProgressInfo {
  if (role === "seller") {
    const currentStep = getSellerStepForStatus(status);
    return {
      steps: SELLER_PROGRESS_STEPS,
      labels: SELLER_STEP_LABELS,
      descriptions: SELLER_STEP_DESCRIPTIONS,
      currentStep,
      currentStepIndex: SELLER_PROGRESS_STEPS.indexOf(currentStep),
    };
  } else {
    const currentStep = getBuyerStepForStatus(status);
    return {
      steps: BUYER_PROGRESS_STEPS,
      labels: BUYER_STEP_LABELS,
      descriptions: BUYER_STEP_DESCRIPTIONS,
      currentStep,
      currentStepIndex: BUYER_PROGRESS_STEPS.indexOf(currentStep),
    };
  }
}

// ============================================================================
// Deal Flow Transparency - Supporting Tables Types
// ============================================================================

// Verification Status Enum
export const VerificationStatusEnum = z.enum(["pending", "verified", "failed", "needs_info"]);

export type VerificationStatus = z.infer<typeof VerificationStatusEnum>;

// Signature Status Enum
export const SignatureStatusEnum = z.enum(["pending", "sent", "viewed", "signed", "declined"]);

export type SignatureStatus = z.infer<typeof SignatureStatusEnum>;

// Identity verification status
export const IdentityStatusEnum = z.enum(["pending", "uploaded", "verified", "rejected"]);
export type IdentityStatus = z.infer<typeof IdentityStatusEnum>;

// Deal Verification Schema (Ökokonto document + identity verification)
export const DealVerificationSchema = z.object({
  id: z.string().uuid(),
  deal_id: z.string().uuid(),

  // Ökokonto document upload
  okokonto_document_url: z.string().nullable().optional(),
  okokonto_document_name: z.string().nullable().optional(),
  uploaded_at: z.string().datetime().nullable().optional(),
  uploaded_by: z.string().uuid().nullable().optional(),

  // Verification results (Ökokonto)
  verification_status: VerificationStatusEnum.default("pending"),
  verified_at: z.string().datetime().nullable().optional(),
  verified_by: z.string().uuid().nullable().optional(),

  // Matching checks
  location_match: z.boolean().nullable().optional(),
  location_match_details: z.record(z.string(), z.unknown()).nullable().optional(),
  quantity_match: z.boolean().nullable().optional(),
  quantity_match_details: z.record(z.string(), z.unknown()).nullable().optional(),

  // Feedback
  admin_feedback: z.string().nullable().optional(),

  // Buyer identity verification
  buyer_identity_document_url: z.string().nullable().optional(),
  buyer_identity_document_name: z.string().nullable().optional(),
  buyer_identity_uploaded_at: z.string().datetime().nullable().optional(),
  buyer_identity_uploaded_by: z.string().uuid().nullable().optional(),
  buyer_identity_status: IdentityStatusEnum.default("pending"),

  // Seller identity verification
  seller_identity_document_url: z.string().nullable().optional(),
  seller_identity_document_name: z.string().nullable().optional(),
  seller_identity_uploaded_at: z.string().datetime().nullable().optional(),
  seller_identity_uploaded_by: z.string().uuid().nullable().optional(),
  seller_identity_status: IdentityStatusEnum.default("pending"),

  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type DealVerification = z.infer<typeof DealVerificationSchema>;

// Deal Contract Review Schema (billing addresses and terms acceptance)
export const DealContractReviewSchema = z.object({
  id: z.string().uuid(),
  deal_id: z.string().uuid(),

  // Seller billing address
  seller_billing_company: z.string().nullable().optional(),
  seller_billing_name: z.string().nullable().optional(),
  seller_billing_address_line1: z.string().nullable().optional(),
  seller_billing_address_line2: z.string().nullable().optional(),
  seller_billing_postal_code: z.string().nullable().optional(),
  seller_billing_city: z.string().nullable().optional(),
  seller_billing_country: z.string().default("DE"),
  seller_billing_tax_id: z.string().nullable().optional(),
  seller_billing_phone: z.string().nullable().optional(),
  seller_iban: z.string().nullable().optional(),
  seller_signer_name: z.string().nullable().optional(),
  seller_signer_email: z.string().nullable().optional(),
  seller_terms_accepted: z.boolean().default(false),
  seller_terms_accepted_at: z.string().datetime().nullable().optional(),

  // Buyer billing address
  buyer_billing_company: z.string().nullable().optional(),
  buyer_billing_name: z.string().nullable().optional(),
  buyer_billing_address_line1: z.string().nullable().optional(),
  buyer_billing_address_line2: z.string().nullable().optional(),
  buyer_billing_postal_code: z.string().nullable().optional(),
  buyer_billing_city: z.string().nullable().optional(),
  buyer_billing_country: z.string().default("DE"),
  buyer_billing_tax_id: z.string().nullable().optional(),
  buyer_billing_phone: z.string().nullable().optional(),
  buyer_signer_name: z.string().nullable().optional(),
  buyer_signer_email: z.string().nullable().optional(),
  buyer_terms_accepted: z.boolean().default(false),
  buyer_terms_accepted_at: z.string().datetime().nullable().optional(),

  // Contract preview
  contract_preview_url: z.string().nullable().optional(),
  contract_preview_generated_at: z.string().datetime().nullable().optional(),

  // Contract document (uploaded by admin)
  contract_document_url: z.string().nullable().optional(),
  contract_document_name: z.string().nullable().optional(),
  contract_uploaded_at: z.string().datetime().nullable().optional(),
  contract_uploaded_by: z.string().uuid().nullable().optional(),

  // Contract draft acceptance (contract_review step)
  seller_contract_accepted: z.boolean().default(false),
  seller_contract_accepted_at: z.string().datetime().nullable().optional(),
  buyer_contract_accepted: z.boolean().default(false),
  buyer_contract_accepted_at: z.string().datetime().nullable().optional(),
  seller_contract_feedback: z.string().nullable().optional(),
  buyer_contract_feedback: z.string().nullable().optional(),

  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type DealContractReview = z.infer<typeof DealContractReviewSchema>;

// Deal Signatures Schema (DocuSign envelope tracking)
export const DealSignaturesSchema = z.object({
  id: z.string().uuid(),
  deal_id: z.string().uuid(),

  // DocuSign integration
  docusign_envelope_id: z.string().nullable().optional(),
  docusign_envelope_status: z.string().nullable().optional(),
  docusign_created_at: z.string().datetime().nullable().optional(),
  docusign_sent_at: z.string().datetime().nullable().optional(),
  docusign_completed_at: z.string().datetime().nullable().optional(),
  docusign_error_message: z.string().nullable().optional(),

  // Seller signature
  seller_signature_status: SignatureStatusEnum.default("pending"),
  seller_signed_at: z.string().datetime().nullable().optional(),
  seller_ip_address: z.string().nullable().optional(),

  // Buyer signature
  buyer_signature_status: SignatureStatusEnum.default("pending"),
  buyer_signed_at: z.string().datetime().nullable().optional(),
  buyer_ip_address: z.string().nullable().optional(),

  // Final signed document
  signed_contract_url: z.string().nullable().optional(),

  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type DealSignatures = z.infer<typeof DealSignaturesSchema>;

// Deal Payments Schema (4-checkbox payment tracking)
export const DealPaymentsSchema = z.object({
  id: z.string().uuid(),
  deal_id: z.string().uuid(),

  // Payment amounts
  total_amount: z.number().nullable().optional(),
  buyer_commission_amount: z.number().nullable().optional(),
  seller_payout_amount: z.number().nullable().optional(),
  admin_commission_amount: z.number().nullable().optional(),

  // 4 checkboxes for payment tracking
  buyer_payment_received: z.boolean().default(false),
  buyer_payment_received_at: z.string().datetime().nullable().optional(),
  buyer_payment_received_by: z.string().uuid().nullable().optional(),

  buyer_commission_received: z.boolean().default(false),
  buyer_commission_received_at: z.string().datetime().nullable().optional(),
  buyer_commission_received_by: z.string().uuid().nullable().optional(),

  seller_payment_sent: z.boolean().default(false),
  seller_payment_sent_at: z.string().datetime().nullable().optional(),
  seller_payment_sent_by: z.string().uuid().nullable().optional(),

  admin_commission_received: z.boolean().default(false),
  admin_commission_received_at: z.string().datetime().nullable().optional(),
  admin_commission_received_by: z.string().uuid().nullable().optional(),

  // Bank details / notes
  bank_reference: z.string().nullable().optional(),
  payment_notes: z.string().nullable().optional(),

  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type DealPayments = z.infer<typeof DealPaymentsSchema>;

/** D15: consumes demand, independent of stepper ordering; pinned to SQL. */
export const RESERVING_DEAL_STATUSES: DealStatus[] = [
  "reserved",
  "inquiry_pending",
  "verification",
  "proposal_ready",
  "contract_review",
  "awaiting_signatures",
  "unb_pending",
  "payment_processing",
  "completed",
];
